/**
 * TutorialInterceptor.js
 *
 * A single proxy layer that routes AI service calls to TutorialMockProvider
 * when a tutorial project is active, or to the real service otherwise.
 *
 * Usage in MainPage.js:
 *   import * as Tutorial from '../services/TutorialInterceptor';
 *   const result = await Tutorial.generateScreenplayOutline(story, model, true, isTutorial);
 *
 * Adding a new interceptable call: add a wrapper function below — no other files need changing.
 */

import { TutorialMockProvider } from './TutorialMockProvider';
import {
    generateScreenplayOutline as _generateScreenplayOutline,
    generateCharacterBibleStrict as _generateCharacterBibleStrict,
    enrichCharacters as _enrichCharacters,
    getScreenplayDetailJob as _getScreenplayDetailJob,
} from './backend/textService.backend';
import {
    ImageAPI as _ImageAPI,
} from './backend/imageService.backend';
import {
    createVideoJob as _createVideoJob,
    getVideoJob as _getVideoJob,
    generateSceneShotVideos as _generateSceneShotVideos,
    submitTranscoderMerge as _submitTranscoderMerge,
    getTranscoderJob as _getTranscoderJob,
} from './backend/videoService.backend';

export let isTutorialModeActive = false;

export function setTutorialMode(isActive) {
    isTutorialModeActive = isActive;
    console.log(`[TutorialInterceptor] Tutorial mode set to: ${isActive}`);
}

// ─── Text ────────────────────────────────────────────────────────────────────

export async function generateScreenplayOutline(titleObj, model, backgroundDetails = true) {
    if (isTutorialModeActive) return TutorialMockProvider.generateScreenplayOutline(titleObj, model);
    return _generateScreenplayOutline(titleObj, model, backgroundDetails);
}

export async function getScreenplayDetailJob(jobId) {
    if (jobId?.startsWith('mock_tutorial_')) return TutorialMockProvider.getScreenplayDetailJob(jobId);
    return _getScreenplayDetailJob(jobId);
}

export async function generateCharacterBibleStrict(payload) {
    if (isTutorialModeActive) {
        const { titleObj, model, outline } = payload || {};
        return TutorialMockProvider.generateCharacterBibleJson(titleObj, model, outline);
    }
    return _generateCharacterBibleStrict(payload);
}

export async function enrichCharacters(params) {
    if (isTutorialModeActive) return { characterMap: params.characterMap };
    return _enrichCharacters(params);
}


// ─── Image ───────────────────────────────────────────────────────────────────

export const ImageAPI = {
    // Pass-through all methods; only sceneWithRefs has a tutorial intercept
    ..._ImageAPI,

    sceneWithRefs(params = {}) {
        const { scene_number, ...rest } = params;
        if (isTutorialModeActive) return TutorialMockProvider.sceneWithRefs({ scene_number });
        return _ImageAPI.sceneWithRefs(rest);
    },
};


// ─── Video ───────────────────────────────────────────────────────────────────

export async function createVideoJob(scenePrompt, imageUrl, options = {}, model = 'veo') {
    if (isTutorialModeActive) return TutorialMockProvider.createVideoJob(scenePrompt, imageUrl, options, model);
    return _createVideoJob(scenePrompt, imageUrl, options, model);
}

export async function getVideoJob(jobId) {
    if (jobId?.startsWith('mock_tutorial_')) return TutorialMockProvider.getVideoJob(jobId);
    return _getVideoJob(jobId);
}

export async function generateSceneShotVideos(params = {}) {
    const { scene, ...rest } = params;
    if (isTutorialModeActive) {
        const shotsList = (scene?.shot_matrix || []).flatMap(b => b.shots || []);
        return TutorialMockProvider.generateSceneShotVideos({ shotsList });
    }
    return _generateSceneShotVideos({ scene, ...rest });
}

export async function submitTranscoderMerge(videoUrls, outputName, geometry) {
    if (isTutorialModeActive) return TutorialMockProvider.submitTranscoderMerge(videoUrls, outputName, geometry);
    return _submitTranscoderMerge(videoUrls, outputName, geometry);
}

export async function getTranscoderJob(jobId) {
    if (jobId?.startsWith('mock_tutorial_')) return TutorialMockProvider.getTranscoderJob(jobId);
    return _getTranscoderJob(jobId);
}
